import { Separator } from "../components/Separator";
import { cn } from "../lib/utils";
import React from "react";
import { Button } from "./Button";
/**
 * A page-scaffolding namespace that stacks standardized content blocks with
 * consistent spacing: Page.Header, Page.SectionHeader, typography helpers
 * Page.P / Page.H, and arrangement helpers Page.Layout / Page.Horizontal /
 * Page.Vertical / Page.Fluid. Use it to build the vertical structure of a
 * settings, detail, or overview page with uniform rhythm.
 * @summary Standardized page scaffolding blocks.
 */
export function Page({ children, variant = "normal" }) {
    const mainVariantClasses = variant === "normal" ? "h-full py-16" : "h-full py-4 px-2";
    const divVariantClassNames = variant === "normal" ? "gap-6 px-6" : "gap-4";
    return (React.createElement("main", { className: mainVariantClasses },
        React.createElement("div", { className: cn("mx-auto flex h-full max-w-4xl flex-col text-sm font-normal", "text-foreground", divVariantClassNames) }, children)));
}
/** The page's top header: title with optional description, used once at the top of a Page. */
Page.Header = function ({ title, description, noTopPadding }) {
    return (React.createElement(Page.Vertical, { gap: "xs", className: noTopPadding ? undefined : "pt-4 sm:pt-6 md:pt-8" },
        typeof title === "string" ? (React.createElement(Page.H, { variant: "h3" }, title)) : (React.createElement(React.Fragment, null, title)),
        description && React.createElement(Page.P, { variant: "secondary" }, description)));
};
/** A section delimiter: title, description, and an optional CTA via `action`. */
Page.SectionHeader = function ({ title, description, action, }) {
    return (React.createElement(Page.Horizontal, { gap: "md" },
        React.createElement(Page.Vertical, { gap: "xs", sizing: "grow" },
            React.createElement(Page.H, { variant: "h5" }, title),
            React.createElement(Page.P, { variant: "secondary" }, description)),
        action && (React.createElement("div", null,
            React.createElement(Button, { ...action })))));
};
/** A horizontal divider between page blocks. */
Page.Separator = function () {
    return React.createElement(Separator, null);
};
const PsizeClasses = {
    xs: "copy-xs",
    sm: "copy-sm",
    md: "copy-base",
    lg: "copy-lg",
};
/** A paragraph with standardized sizing (`size`) and a `primary`/`secondary` color variant. */
Page.P = function ({ children, variant, size = "sm" }) {
    return (React.createElement("p", { className: cn(PsizeClasses[size], variant === "secondary" ? "text-muted-foreground" : "text-foreground") }, children));
};
/** A heading rendered as the given `variant` tag (h1-h6) with the matching type scale. */
Page.H = function ({ children, variant = "h3" }) {
    const Component = variant;
    const hSizes = {
        h1: "heading-4xl",
        h2: "heading-3xl",
        h3: "heading-2xl",
        h4: "heading-xl",
        h5: "heading-lg",
        h6: "heading-base",
    };
    return (React.createElement(Component, { className: cn("text-foreground", hSizes[variant]) }, children));
};
const gapSizes = {
    xs: "gap-1",
    sm: "gap-2",
    md: "gap-3",
    lg: "gap-5",
    xl: "gap-8",
    none: "",
};
/** Arranges child blocks by `direction`, delegating to Page.Horizontal / Page.Vertical / Page.Fluid. */
Page.Layout = function ({ children, direction = "vertical", sizing, align = "stretch", gap = "lg", }) {
    switch (direction) {
        case "horizontal":
            return (React.createElement(Page.Horizontal, { children: children, sizing: sizing, align: align, gap: gap }));
        case "vertical":
            return (React.createElement(Page.Vertical, { children: children, sizing: sizing, align: align, gap: gap }));
        case "fluid":
            return (React.createElement(Page.Fluid, { children: children, sizing: sizing, align: align, gap: gap }));
        default:
            return null;
    }
};
/** A horizontal row of blocks (stacks vertically on small screens). */
Page.Horizontal = function ({ children, sizing, align = "left", gap = "lg", }) {
    return (React.createElement("div", { className: cn("flex flex-col sm:flex-row", sizing === "grow" ? "grow basis-0" : "", sizing === "shrink" ? "shrink" : "", gapSizes[gap], align === "left" ? "justify-start" : "", align === "center" ? "justify-center" : "", align === "right" ? "justify-end" : "", align === "stretch" ? "justify-stretch" : "") }, children));
};
/** A vertical column of blocks. */
Page.Vertical = function ({ children, sizing, align = "left", gap = "md", className, }) {
    return (React.createElement("div", { className: cn("flex flex-col", sizing === "grow" ? "grow basis-0" : "", sizing === "shrink" ? "shrink" : "", gapSizes[gap], align === "left" ? "items-start" : "", align === "center" ? "items-center" : "", align === "right" ? "items-end" : "", className) }, children));
};
/** A wrapping row of blocks for fluid, grid-like arrangements. */
Page.Fluid = function ({ children, sizing, align = "stretch", gap = "xs", }) {
    return (React.createElement("div", { className: cn("flex flex-wrap", sizing === "grow" ? "grow" : "", sizing === "shrink" ? "shrink" : "", gapSizes[gap], align === "left" ? "items-start" : "", align === "center" ? "items-center" : "", align === "right" ? "items-end" : "") }, children));
};
//# sourceMappingURL=Page.js.map